import { useState, useRef } from "react";
import toast from "react-hot-toast";
import { uploadSuppliersExcel } from "../api/suppliers";

export default function SuppliersUploadModal({
  isOpen,
  onClose,
  onSuccess,
  canUpload = true,
}) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const validateAndSetFile = (selectedFile) => {
    setError("");

    if (!selectedFile?.name?.match(/\.(xlsx|xls)$/i)) {
      setError("Only .xlsx or .xls files allowed");
      return false;
    }

    if (selectedFile.size > 15 * 1024 * 1024) {
      setError("File too large (max 15 MB)");
      return false;
    }

    setFile(selectedFile);
    return true;
  };

  const handleFileChange = (e) => {
    if (!canUpload) return;
    const selected = e.target.files?.[0];
    if (selected) validateAndSetFile(selected);
  };

  const handleDrop = (e) => {
    if (!canUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleUpload = async () => {
    if (!canUpload) {
      toast.error("You do not have permission to upload suppliers.");
      return;
    }
    if (!file) return;

    if (
      !window.confirm(
        "This will update the supplier database with the uploaded file.\n\nContinue?",
      )
    ) {
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No authentication token found. Please log in again.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await uploadSuppliersExcel(file, token);
      toast.success(res.message || "Suppliers updated successfully");
      onSuccess?.();
      onClose();
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const msg =
        err?.response?.data?.message || err.message || "Upload failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Update Suppliers
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload supplier Excel file to update the list
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div
            className={`
              border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
              ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}
              ${file ? "bg-green-50 border-green-300" : ""}
            `}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ pointerEvents: canUpload ? "auto" : "none" }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="mx-auto w-16 h-16 text-gray-400 mb-4">
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>

            {file ? (
              <div className="space-y-1">
                <p className="font-medium text-green-700">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <>
                <p className="font-medium text-gray-900">
                  <span className="text-blue-600 hover:underline">
                    Click to browse
                  </span>{" "}
                  or drag & drop
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  XLSX or XLS • max 15 MB
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-200 text-gray-800 font-medium rounded-xl hover:bg-gray-300 transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={loading || !file || !canUpload}
            className={`
              px-6 py-2.5 font-semibold rounded-xl flex items-center gap-2 transition
              ${loading || !file || !canUpload
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-black text-white hover:bg-gray-900 active:bg-gray-950 shadow-md"
              }
            `}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
